import React from "react";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, Loader2, AlertCircle } from "lucide-react";
import { driverApi, API } from "../lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const supported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export default function DriverPushToggle() {
  const [permission, setPermission] = React.useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  // Initial check: are we already subscribed in this browser?
  React.useEffect(() => {
    if (!supported()) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch (_) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = async () => {
    if (!supported()) {
      toast.error("Din browser understøtter ikke push-notifikationer");
      return;
    }
    setLoading(true);
    try {
      // 1. Permission
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Tilladelse ikke givet — aktivér i browser-indstillinger");
        return;
      }
      // 2. Service worker ready
      const reg = await navigator.serviceWorker.ready;
      // 3. VAPID public key from backend
      const { data } = await driverApi().get("/driver/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey);
      // 4. Subscribe (or reuse)
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      }
      const json = sub.toJSON();
      // 5. Persist on backend
      await driverApi().post("/driver/push/subscribe", {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setSubscribed(true);
      toast.success("Notifikationer aktiveret");
    } catch (e) {
      console.error("Push enable failed", e);
      const msg = e?.response?.data?.detail || e?.message || "Kunne ikke aktivere notifikationer";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    if (!supported()) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const json = sub.toJSON();
        await sub.unsubscribe();
        try {
          await driverApi().post("/driver/push/unsubscribe", {
            endpoint: json.endpoint,
            keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
          });
        } catch (_) { /* best-effort */ }
      }
      setSubscribed(false);
      toast.success("Notifikationer deaktiveret");
    } catch (e) {
      toast.error("Kunne ikke deaktivere");
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await driverApi().post("/driver/push/test");
      if (data.sent > 0) toast.success(`Testbesked sendt (${data.sent})`);
      else toast.error("Ingen aktive abonnementer fundet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fejlede");
    } finally {
      setBusy(false);
    }
  };

  if (!supported()) {
    return (
      <div className="bg-amber-50 border border-amber-300 px-4 py-3 flex items-start gap-3" data-testid="push-unsupported">
        <AlertCircle size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900">
          <strong>Push-notifikationer ikke understøttet i denne browser.</strong>
          <div className="mt-1">På iPhone: Tilføj appen til hjemmeskærm via Del-knappen i Safari, og åbn den derfra.</div>
        </div>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div className="bg-red-50 border border-red-300 px-4 py-3 flex items-start gap-3" data-testid="push-denied">
        <BellOff size={18} className="text-red-700 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-red-900">
          <strong>Notifikationer er blokeret af browseren.</strong>
          <div className="mt-1">Åbn browser-indstillinger → tilladelser → notifikationer → tillad emakurer.dk.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`border ${subscribed ? "bg-emerald-50 border-emerald-300" : "bg-white border-brand"} px-4 py-3 flex items-center justify-between gap-3`} data-testid="push-toggle">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {subscribed ? (
          <BellRing size={18} className="text-emerald-700 flex-shrink-0 mt-0.5" />
        ) : (
          <Bell size={18} className="text-brand-text2 flex-shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <div className={`text-sm font-semibold ${subscribed ? "text-emerald-900" : "text-brand-dark"}`}>
            {subscribed ? "Notifikationer aktive" : "Aktivér ordre-notifikationer"}
          </div>
          <div className={`text-xs mt-0.5 ${subscribed ? "text-emerald-800/80" : "text-brand-text2"}`}>
            {subscribed
              ? "Du modtager besked når en kunde booker og betaler"
              : "Få besked på telefonen når der kommer en ny ordre"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {subscribed && (
          <button
            type="button"
            onClick={sendTest}
            disabled={busy}
            className="text-[10px] uppercase tracking-wider font-bold border border-emerald-700 text-emerald-700 px-3 py-2 hover:bg-emerald-700 hover:text-white transition-colors disabled:opacity-50"
            data-testid="push-test-btn"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : "Test"}
          </button>
        )}
        <button
          type="button"
          onClick={subscribed ? disable : enable}
          disabled={loading}
          className={`text-xs uppercase tracking-wider font-bold px-4 py-2 transition-colors disabled:opacity-50 ${
            subscribed
              ? "border border-red-700 text-red-700 hover:bg-red-700 hover:text-white"
              : "bg-brand-red text-white hover:bg-brand-dark"
          }`}
          data-testid={subscribed ? "push-disable-btn" : "push-enable-btn"}
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : (subscribed ? "Slå fra" : "Aktivér")}
        </button>
      </div>
    </div>
  );
}
